import { gameLeave } from '@/api/game'
import { showNotice } from '@/utils/tool'
import store from 'vuex-store/index'
import i18n from '@/i18n'

// type = game or provider
export function gameStart(type, gameOrProvider, selectedCurrency) {
  if (!store.getters.token) {
    // 未登入
    store.commit('SET_LOGIN_OPEN', true)
  } else if (type === 'game' && gameOrProvider.status === 2) {
    // 遊戲維護中
    showNotice(i18n.t('Bao.game_maintenance'))
  } else {
    // 實際進入遊戲
    if (selectedCurrency) {
      store.dispatch('launchGame', { type, gameOrProvider, selectedCurrency })
    } else {
      store.dispatch('launchGame', { type, gameOrProvider })
    }
  }
}

export function gameEnd(game_id) {
  if (!store.getters.token) {
    return
  }
  // loading開始
  store.commit('SET_LOADING', true)

  store.commit('SET_IN_GAME_ID', null)
  if (game_id) {
    showNotice('closeModal')

    gameLeave(game_id).then(response => {
      // 寫入遊戲餘額
      const current = store.getters.gameBalance
      const gameBalance = response.data.data
      current[gameBalance.game_provider_id] = gameBalance
      store.commit('SET_GAME_BALANCE_INFO', current)

      // loading結束
      store.commit('SET_LOADING', false)
      store.commit('SET_CURRENT_GAME', {})
    })
  } else {
    store.commit('SET_LOADING', false)
    store.commit('SET_CURRENT_GAME', {})
  }
}

export function showGameWalletModal(gameProviderId, fixType, callback) {
  const data = {
    gameProviderId: gameProviderId,
    fixType: fixType === undefined ? false : fixType,
    callback: callback
  }

  store.commit('SET_GAME_WALLET_MODAL', data)
}

export function hideGameWalletModal() {
  store.commit('SET_GAME_WALLET_MODAL', false)
}

// 開遊戲的時候要多帶參數 wallet_type、currency_in_game
export async function launchGameWithProviderInfo(game, selectedCurrency = null) {
  const gameProviders = store.state.game.gameProvider

  if (gameProviders) {
    const provider = gameProviders.find(p => p.id === game.game_provider_id)
    if (provider) {
      game.wallet_type = provider.support_wallet_type
      game.currency_in_game = provider.support_currency
      store.commit('SET_CURRENT_GAME', game)
    }
  }

  // wallet_type 檢查
  const canProceed = await checkWalletCompatibility(game)
  if (!canProceed) return

  // wallet_type 支持，才顯示貨幣選擇彈窗或直接開遊戲
  if (selectedCurrency) {
    await continueGameLaunchWithCurrency(selectedCurrency)
  } else {
    store.commit('SET_PENDING_GAME_DATA', {
      type: 'game',
      gameOrProvider: game
    })
  }
}

// New function to continue game launch after currency selection
export async function continueGameLaunchWithCurrency(selectedCurrency) {
  store.commit('SET_CURRENCY_CHANGE_IN_PROGRESS', true)

  try {
    const pendingGame = store.state.app.pendingGameData
    const currentGame = store.state.app.currentGame
    const gameUrl = store.state.app.gameUrl

    if (pendingGame) {
      await gameStart(pendingGame.type, pendingGame.gameOrProvider, selectedCurrency)
    } else if (currentGame && Object.keys(currentGame).length > 0 && gameUrl) {
      // Clear the current game URL to trigger game end and component unmounting
      store.commit('SET_GAME_URL', '')
      // Wait a bit for the game to end properly and component to unmount
      await new Promise(resolve => setTimeout(resolve, 800))
      // Relaunch the game with the new currency
      await gameStart('game', currentGame, selectedCurrency)
    }
  } catch (error) {
    console.error('Error during currency change:', error)
    throw error
  } finally {
    // Always reset the currency change progress state
    store.commit('SET_CURRENCY_CHANGE_IN_PROGRESS', false)
  }
}

/**
 * 檢查當前 wallet_type 是否支援該遊戲
 * 不支援時：找同幣別的替代錢包 → 詢問切換 / 顯示錯誤訊息
 * @returns true = 可繼續開遊戲，false = 中止
 */
async function checkWalletCompatibility(game) {
  const supportWalletType = game.support_wallet_type

  if (!Array.isArray(supportWalletType) || supportWalletType.length === 0) {
    return true
  }

  const currentWalletType = store.state.app.selectedWallet?.wallet_type
  if (!currentWalletType) return true

  if (supportWalletType.includes(currentWalletType)) {
    return true
  }

  const brandWalletTypes = store.getters.brandInfo?.wallet_types || []

  const currentWalletDef = brandWalletTypes.find(w => w.wallet_type === currentWalletType)
  const currentCurrency = currentWalletDef?.currency
  const gameName = game.name || ''

  if (!currentCurrency) {
    showNotice(i18n.t('Bao.wallet_not_support', {
      currentWallet: currentWalletType,
      gameName
    }))
    return false
  }

  const alternativeWalletDef = brandWalletTypes.find(w =>
    w.currency === currentCurrency &&
    w.wallet_type !== currentWalletType &&
    supportWalletType.includes(w.wallet_type)
  )

  if (alternativeWalletDef) {
    return new Promise((resolve) => {
      let resolved = false

      const safeResolve = (val) => {
        if (!resolved) {
          resolved = true
        unwatch?.()
        resolve(val)
        }
      }

      const walletTypeI18n = (walletType) => {
        const key = `Bao.wallet_type_${walletType}`
        return i18n.t(key)
      }

      showNotice(
        i18n.t('Bao.wallet_not_support_change', {
          currentWallet: walletTypeI18n(currentWalletType),
          gameName: gameName,
          alternativeWallet: walletTypeI18n(alternativeWalletDef.wallet_type)
        }),
        () => {
          const walletInfo = store.state.user.walletInfo || []
          const targetWallet = walletInfo.find(w => w.wallet_type === alternativeWalletDef.wallet_type)

          if (targetWallet) {
            store.commit('SET_SELECTED_WALLET', targetWallet)
          }

          store.commit('SET_PENDING_GAME_DATA', {
            type: 'game',
            gameOrProvider: game
          })

          safeResolve(false)
        },
        true,
        '',
        i18n.t('Bao.wallet_type_ok')
      )

      const unwatch = store.watch(
        state => state.app.noticeMessage,
        (newVal) => {
          if (newVal === null) {
            safeResolve(false)
          }
        }
      )
    })
  } else {
    showNotice(i18n.t('Bao.wallet_not_support', {
      currentWallet: currentWalletType,
      gameName
    }))
    return false
  }
}
